<!-- eslint-disable max-len -->
<template>
  <!-- NO PLUGINS -->
  <div v-if="props.plugins.length === 0">
    <div class="bg-white">
      <div class="px-6 py-16 sm:px-6 sm:py-32 lg:px-8">
        <div class="mx-auto max-w-2xl text-center">
          <h2 class="text-4xl font-semibold tracking-tight text-balance text-gray-900 sm:text-5xl">
            {{ t('dashboard.plugin.calltoaction.title') }}
          </h2>
          <div class="mt-10 flex items-center justify-center gap-x-6">
            <a
              href="#"
              class="rounded-md bg-teal-500 px-3.5 py-2.5 text-lg font-semibold text-white shadow-xs hover:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              @click.prevent="showCreateModal = true"
            >
              {{ t('dashboard.plugin.calltoaction.button') }}
            </a>
            <a
              href="#"
              class="text-sm/6 font-semibold text-gray-900"
              @click.prevent="showHelpModal = true"
            >
              {{ t('dashboard.plugin.calltoaction.learnmore') }} <span aria-hidden="true">→</span>
            </a>
          </div>
          <p
            class="mx-auto mt-6 max-w-3xl text-lg/8 text-pretty text-gray-600"
            v-html="t('dashboard.plugin.calltoaction.subtitle')"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- LOADING -->
  <div v-if="!dataLoaded">
    <div class="mt-6 grid grid-cols-1">
      <div
        v-for="i in 1"
        :key="i"
        class="border border-gray-300 rounded-lg shadow-sm overflow-hidden bg-white px-4 py-5 sm:p-6 animate-pulse"
      >
        <dt class="truncate text-sm font-medium text-gray-300">
          ...
        </dt>
        <dd class="mt-1 h-8 bg-gray-200 rounded w-3/4" />
      </div>
    </div>
  </div>

  <!-- HAS PLUGINS -->
  <div v-else>
    <ul>
      <li
        v-for="content in sortedMetadataArray"
        :key="content.plugin.name"
      >
        <div class="bg-white shadow-xs rounded-lg p-4 mb-4">
          <PluginHeader
            :plugin="content.plugin"
            :enable-link="true"
          />

          <ListPluginReportsTable
            :plugin="content.plugin"
            :metadatas="content.metadatas"
            :page-limit="pageLimit"
          />
        </div>
      </li>
    </ul>
    <!-- ONE PLUGIN AND NO METADATAS -->
    <div
      v-if="hasOnePluginAndNoMetadatas"
      class="bg-white"
    >
      <div class="px-6 py-16 sm:px-6 sm:py-32 lg:px-8">
        <div class="mx-auto max-w-2xl">
          <h2 class="text-center text-4xl font-semibold tracking-tight text-balance text-gray-900 sm:text-5xl">
            {{ t('dashboard.plugin.initialSetup.title') }}
          </h2>
          <p
            class="mx-auto mt-6 max-w-3xl text-lg/8 text-pretty text-gray-600"
            v-html="t('dashboard.plugin.initialSetup.subtitle')"
          />
          <div class="mt-10 flex items-center justify-center gap-x-6">
            <a
              href="#"
              class="rounded-md bg-teal-500 px-3.5 py-2.5 text-lg font-semibold text-white shadow-xs hover:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              @click.prevent="openPluginLivesPage()"
            >
              {{ t('dashboard.plugin.initialSetup.download-ebook') }}
            </a>
            <a
              href="#"
              class="text-sm/6 font-semibold text-gray-900"
              @click="navigateWhatsapp(t('dashboard.plugin.initialSetup.help'))"
            >
              {{ t('dashboard.plugin.initialSetup.help') }} <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <RightSideDrawer
    v-model:show="showHelpModal"
    bg-color="bg-gray-100"
  >
    <!-- Custom Header -->
    <template #header>
      <DialogTitle class="px-4 py-6 sm:px-6 ">
        <p class="text-base font-semibold text-gray-900">
          {{ t('plugins.manager.modal.help.title') }}
        </p>
        <p class="mt-1 text-md text-gray-900">
          {{ t('plugins.manager.modal.help.description') }}
        </p>
      </DialogTitle>
    </template>

    <!-- Custom Content -->
    <template #content>
      <div class="px-4 py-6 sm:px-6">
        <dl class="space-y-8">
          <div>
            <dt class="text-sm font-medium text-gray-500 sm:shrink-0">
              <strong>{{ t('plugins.manager.modal.help.permissions.title') }}</strong>
              <br>
              <p class="italic text-gray-500 mt-2">
                {{ t('plugins.manager.modal.help.permissions.error') }}
              </p>
            </dt>
            <dd class="text-sm text-gray-900 sm:col-span-2 mt-4">
              <p>{{ t('plugins.manager.modal.help.permissions.solution') }}</p>
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-gray-500 sm:shrink-0">
              <strong>{{ t('plugins.manager.modal.help.admin.title') }}</strong>
              <br>
              <p class="italic text-gray-500 mt-2">
                {{ t('plugins.manager.modal.help.admin.error') }}
              </p>
            </dt>
            <dd class="text-sm text-gray-900 sm:col-span-2 mt-4">
              <p>{{ t('plugins.manager.modal.help.admin.solution') }}</p>
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-gray-500 sm:shrink-0">
              <strong>{{ t('plugins.manager.modal.help.link.title') }}</strong>
              <br>
              <p class="italic text-gray-500 mt-2">
                {{ t('plugins.manager.modal.help.link.error') }}
              </p>
            </dt>
            <dd class="text-sm text-gray-900 sm:col-span-2 mt-4">
              <p>{{ t('plugins.manager.modal.help.link.solution') }}</p>
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-gray-500 sm:shrink-0">
              <strong>{{ t('plugins.manager.modal.help.selection.title') }}</strong>
              <br>
              <p class="italic text-gray-500 mt-2">
                {{ t('plugins.manager.modal.help.selection.error') }}
              </p>
            </dt>
            <dd class="text-sm text-gray-900 sm:col-span-2 mt-4">
              <p>{{ t('plugins.manager.modal.help.selection.solution') }}</p>
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-gray-500 sm:shrink-0">
              <strong>{{ t('plugins.manager.modal.help.personal.title') }}</strong>
              <br>
              <p class="italic text-gray-500 mt-2">
                {{ t('plugins.manager.modal.help.personal.error') }}
              </p>
            </dt>
            <dd class="text-sm text-gray-900 sm:col-span-2 mt-4">
              <p>{{ t('plugins.manager.modal.help.personal.solution') }}</p>
            </dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-gray-500 sm:shrink-0">
              <strong>{{ t('plugins.manager.modal.help.token.title') }}</strong>
              <br>
              <p class="italic text-gray-500 mt-2">
                {{ t('plugins.manager.modal.help.token.error') }}
              </p>
            </dt>
            <dd class="text-sm text-gray-900 sm:col-span-2 mt-4">
              <p>{{ t('plugins.manager.modal.help.token.solution') }}</p>
            </dd>
          </div>
          <div>
            <button
              type="button"
              class="inline-flex w-full shrink-0 items-center justify-center rounded-md bg-teal-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-teal-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 sm:flex-1"
              @click="navigateWhatsapp(t('plugins.manager.modal.help.button1'))"
            >
              {{ t('plugins.manager.modal.help.button1') }}
            </button>
          </div>
        </dl>
      </div>
    </template>
  </RightSideDrawer>

  <PluginConnectDrawer v-model:show="showCreateModal" />
</template>

<script setup lang="ts">
import {
  onBeforeMount, ref, computed,
} from 'vue';
import Plugin from '@/models/plugin/plugin';
import InstagramLiveMetadata from '@/models/plugin/instagramLiveMetadata';
import FacebookLiveMetadata from '@/models/plugin/facebookLiveMetadata';
import usePluginStore from '@/store/plugin/plugin';
import RightSideDrawer from '@/components/tailwind/shared/RightSideDrawer.vue';
import ListPluginReportsTable from '@/components/tailwind/plugin/ListPluginReportsTable.vue';
import PluginHeader from '@/components/tailwind/plugin/PluginHeader.vue';
import PluginConnectDrawer from '@/components/tailwind/plugin/PluginConnectDrawer.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const { t } = useI18n();
const {
  pluginGetInstagramLiveMetadatas,
  pluginGetFacebookLiveMetadatas,
} = usePluginStore();

/*
  Props
*/
const props = defineProps({
  plugins: {
    type: () => [] as Plugin[],
    required: true,
  },
});

/*
  Data
*/
const dataLoaded = ref(false);
const pageLimit = 5;
const showHelpModal = ref(false);
const showCreateModal = ref(false);
const metadataHash = ref({} as Record<string, {
  plugin: Plugin, metadatas: (InstagramLiveMetadata | FacebookLiveMetadata)[]
}>);

/*
  Computed
*/
const sortedMetadataArray = computed(() => Object.values(metadataHash.value).sort((a, b) => {
  const aCreatedAt = a.metadatas && a.metadatas.length > 0 ? a.metadatas[0]?.createdAt : null;
  const bCreatedAt = b.metadatas && b.metadatas.length > 0 ? b.metadatas[0]?.createdAt : null;

  const aDate = aCreatedAt ? new Date(aCreatedAt) : null;
  const bDate = bCreatedAt ? new Date(bCreatedAt) : null;

  const aHasValidDate = aDate && !Number.isNaN(aDate.getTime());
  const bHasValidDate = bDate && !Number.isNaN(bDate.getTime());

  // Case 1: Both have valid dates
  if (aHasValidDate && bHasValidDate) {
    if (aDate.getTime() !== bDate.getTime()) {
      return bDate.getTime() - aDate.getTime(); // Sort by date descending
    }
    // Dates are equal, fall through to sort by name ascending (as a tie-breaker)
  } else if (aHasValidDate && !bHasValidDate) {
    return -1; // 'a' comes first
  } else if (!aHasValidDate && bHasValidDate) {
    return 1; // 'b' comes first
  }
  // Case 4: Neither has a valid date (or dates were equal and need tie-breaking)
  // Fall back to sorting by plugin.name ascending
  return a.plugin.name.localeCompare(b.plugin.name);
}));

const hasOnePluginAndNoMetadatas = computed(
  () => Object.keys(metadataHash.value).length === 1
  && metadataHash.value[Object.keys(metadataHash.value)[0]]?.metadatas.length === 0,
);

/*
  Methods
*/
const openPluginLivesPage = async () => {
  await router.push({
    name: 'PluginDashboardPage',
    params: {
      pluginId: props.plugins[0].id,
      pluginType: props.plugins[0].type,
    },
    query: {
      tab: 'lives',
    },
  });
};
const navigateWhatsapp = (message: string) => {
  window.open(`https://wa.me/+5511994991786?text=${message}`, '_blank');
};

const loadMetadatas = async (plugin: Plugin) => {
  const fetchMetadatas = plugin.type === 'facebook'
    ? pluginGetFacebookLiveMetadatas({
      pluginId: `${plugin.id}`,
      page: 1,
      limit: pageLimit,
      orderBy: 'createdAt',
      order: -1,
    })
    : pluginGetInstagramLiveMetadatas({
      pluginId: `${plugin.id}`,
      page: 1,
      limit: pageLimit,
      orderBy: 'createdAt',
      order: -1,
    });

  await fetchMetadatas.then((response) => {
    if (response) {
      metadataHash.value[plugin.name] = {
        plugin,
        metadatas: response.data,
      };
    }
  }).catch((error) => {
    console.error('error', error);
  });
};

/*
  Hooks
*/
const loadPluginsInBatches = async () => {
  const batchSize = 30;
  // eslint-disable-next-line no-await-in-loop
  for (let i = 0; i < props.plugins.length; i += batchSize) {
    const batch = props.plugins.slice(i, i + batchSize);
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(batch.map((plugin: Plugin) => loadMetadatas(plugin)));
  }
  dataLoaded.value = true;
};

onBeforeMount(() => {
  if (props.plugins.length === 0) {
    dataLoaded.value = true;
  } else {
    loadPluginsInBatches();
  }
});
</script>
